/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Line, Pie, Bar } from '@ant-design/plots';
import { Radio } from 'antd';

import { ChartCard, RankBars, SectionCard } from '../../../ui';
import styles from '../board.less';
import { buildUserRecordHref } from '../../../utils/userRecord';

const TOP_FILTERS = {
  monthVisit: {
    label: '本月到店',
    valueField: 'visitDays',
    subtitle: '按到店天数排行',
  },
  weekVisit: {
    label: '本周到店',
    valueField: 'visitDays',
    subtitle: '按到店天数排行',
  },
  monthOnlineClock: {
    label: '本月线上打卡',
    valueField: 'clockDays',
    subtitle: '按线上打卡天数排行',
  },
  weekOnlineClock: {
    label: '本周线上打卡',
    valueField: 'clockDays',
    subtitle: '按线上打卡天数排行',
  },
};

/** 4. 会员行为（活跃趋势 / 到店频次 / 打卡行为 / 活跃 TOP） */
export const BehaviorPane = ({ visit, loading, error, onRetry }) => {
  const [topDimension, setTopDimension] = useState('store');
  const [topRange, setTopRange] = useState('month');
  const topKey =
    topDimension === 'store'
      ? topRange === 'month'
        ? 'monthVisit'
        : 'weekVisit'
      : topRange === 'month'
      ? 'monthOnlineClock'
      : 'weekOnlineClock';
  const topFilter = TOP_FILTERS[topKey] || TOP_FILTERS.monthVisit;
  const trend = visit.activeTrend || {};
  const trendData = (trend.labels || []).reduce(
    (acc, label, i) => [
      ...acc,
      { label, type: '到店', value: (trend.visit || [])[i] || 0 },
      {
        label,
        type: '仅线上打卡未到店',
        value: (trend.onlineOnly || [])[i] || 0,
      },
    ],
    [],
  );
  const topList = ((visit.activeTop || {})[topKey] || []).map((m) => ({
    key: m?.userId,
    label: m?.nickName || '--',
    value: m?.[topFilter.valueField] ?? 0,
    unit: ' 天',
    href: buildUserRecordHref(m?.userId),
  }));

  return (
    <>
      <div className={styles.chartRow}>
        <SectionCard
          title="会员活跃趋势"
          subtitle="B 端口径 · 近 30 天 · 截止昨日 · 到店 vs 仅线上打卡"
          loading={loading}
          error={error}
          onRetry={onRetry}
        >
          <ChartCard height={320} empty={!trendData.length} loading={loading}>
            <Line
              height={320}
              data={trendData}
              xField="label"
              yField="value"
              seriesField="type"
              legend={{ position: 'top-left' }}
              xAxis={{ label: { autoHide: true } }}
              smooth
            />
          </ChartCard>
        </SectionCard>

        <SectionCard
          title="到店频次分层"
          subtitle="近 30 天会员活跃分布（人数去重 · 含未到店线上打卡）"
          loading={loading}
          error={error}
          onRetry={onRetry}
        >
          <ChartCard height={300} empty={!(visit.frequencyLevel || []).length}>
            <Pie
              angleField="value"
              colorField="name"
              data={visit.frequencyLevel || []}
              radius={0.82}
              innerRadius={0.58}
              appendPadding={8}
              /* 4 个长名称图例放底部会挤成带「1/2 ▼」的翻页，改右侧竖排 */
              legend={{ position: 'right' }}
              label={false}
              statistic={false}
              tooltip={{
                formatter: (d) => ({ name: d.name, value: `${d.value} 人` }),
              }}
            />
          </ChartCard>
        </SectionCard>
      </div>

      <div className={styles.chartRow}>
        <SectionCard
          title="打卡行为洞察"
          subtitle="今日到店后行为构成"
          loading={loading}
          error={error}
          onRetry={onRetry}
        >
          <ChartCard
            height={280}
            empty={!(visit.clockBehavior || []).length}
            emptyText="暂无数据"
          >
            <Bar
              height={280}
              data={visit.clockBehavior || []}
              xField="value"
              yField="name"
              legend={false}
            />
          </ChartCard>
        </SectionCard>

        <SectionCard
          title="会员活跃 TOP"
          subtitle={topFilter.subtitle}
          loading={loading}
          error={error}
          onRetry={onRetry}
          extra={
            <div className={styles.topFilters}>
              <Radio.Group
                size="small"
                optionType="button"
                buttonStyle="solid"
                value={topDimension}
                options={[
                  { value: 'store', label: '到店' },
                  { value: 'online', label: '线上打卡' },
                ]}
                onChange={(event) => setTopDimension(event.target.value)}
              />
              <Radio.Group
                size="small"
                optionType="button"
                buttonStyle="solid"
                value={topRange}
                options={[
                  { value: 'month', label: '本月' },
                  { value: 'week', label: '本周' },
                ]}
                onChange={(event) => setTopRange(event.target.value)}
              />
            </div>
          }
        >
          <RankBars items={topList} emptyText="暂无排行数据" />
        </SectionCard>
      </div>
    </>
  );
};

export default BehaviorPane;
